import { map, startWith } from 'rxjs';
import {
  Input,
  Output,
  Directive,
  QueryList,
  HostBinding,
  EventEmitter,
  ContentChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * enums
 */
import { IMediaSize } from '@core/enums/media-sizes';

/**
 * helpers
*/
import { SubscriptionTracker } from '@core/utilities';

/**
 * directives
 */
import { ListGroupItemDirective } from './list-group-item.directive';

@Directive({
  selector: '[appListGroup]',
  // providers: [ListGroupItemDirective],
  host: {
    '[class.list-group]': 'true',
  }
})
export class ListGroupDirective {

  //--------------------------------------------------------------------------
  // Fields
  //--------------------------------------------------------------------------  

  /**
   * @description to Tracker one instance and prevent duplicated
   */
  private tracker = new SubscriptionTracker<ListGroupItemDirective>('ListGroupItem');

  //--------------------------------------------------------------------------
  // Decorators 
  //--------------------------------------------------------------------------    

  /**
   * @property items Accessor for projected ListGroupItemDirective instances
   */
  @ContentChildren(ListGroupItemDirective)
  public set items(_items: QueryList<ListGroupItemDirective>) {

    // void
    if (_items == null) return;
    
    this._items = _items;

    // watch item active observable
    this.items
      ?.forEach(item => {
        
        // void previous instance
        if (this.tracker.hasInstance(item)) return;

        // append subscriber on each item
        this.tracker.once(
          item,
          () => item.activeItemChange.asObservable(),
          _item => {

            if (this.isTabpanel) {
              this._handlerTargetItem(item)
            }

            this.itemChange.emit(_item);

          },
          this.isTabpanel && item.itemValue == this.listGroupValue
          ? startWith(item)
          : map(_ => _),
          takeUntilDestroyed(item.destroyRef),
        )
      })

  }
  public get items(): QueryList<ListGroupItemDirective> {
    return this._items
  }

  /**
   * @property _items Internal backing field for ContentChildren
   */  
  private _items!: QueryList<ListGroupItemDirective>

  /**
   * @output Emits when the active item changes
   */
  @Output('itemChange')
  public itemChange = new EventEmitter<ListGroupItemDirective>();

  /**
   * @input Bound value for the list group, used in tabPanel context
   */
  @Input({ alias: 'listGroupValue' })
  public listGroupValue!: string | number
    
  /**
   * @output Emits on value change (two-way binding)
   */
  @Output('listGroupValueChange')
  public listGroupValueChange = new EventEmitter<string | number>();
  
  /**
   * @input Applies Bootstrap flush style
   */
  @HostBinding('class.list-group-flush')
  @Input({ alias: 'flush' })
  public isFlush: boolean = false;

  /**
   * @input Applies Bootstrap numbered style
   */
  @HostBinding('class.list-group-numbered')
  @Input({ alias: 'numbered' })
  public isNumbered: boolean = false;

  /**
   * @input Applies custom timeline styling
   */
  @HostBinding('class.list-group-timeline')
  @Input({ alias: 'timeline' })
  public isTimeline: boolean = false;

  /**
   * @input Applies responsive horizontal list-group class
   */
  @HostBinding('class')
  @Input({
    alias: 'horizontal',
    transform: (value: boolean | Array<IMediaSize>) => {

      // init
      const _baseClassName = 'list-group-horizontal';

      if (Array.isArray(value)) {

        return value.map(size => (`${_baseClassName}-${size}`)).join(" ")

      }

      return value ? _baseClassName : null;

    }
  })
  public horizontal!: Boolean | Array<IMediaSize>

  /**
   * @input Enables tab panel behavior for managing active state by value
   */
  @Input({ alias: 'tabpanel' })
  public isTabpanel: boolean = false;

  /**
   * 
   */
  constructor() {}

  /**
   * @description Handles selection logic when an item is activated
   */
  private _handlerTargetItem(item: ListGroupItemDirective): void {

    // void
    if (item == null) return;

    // 
    this.inActiveNotItemTarget(item);

    if (this.isTabpanel) item.activeItem();

    // 
    this.listGroupValueChange.emit(item.itemValue)

  }

  /**
   * @description Deactivates all items except the selected one
   */
  public inActiveNotItemTarget(item: ListGroupItemDirective): void {

    // find target tab to active it
    // end in active un target tabs
    this.items
          .forEach(_item => {
            if (_item !== item) _item.inActiveItem();
          })

  }

}
